import { useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { api, type PlaysWhere } from "../lib/api";
import { Card, Notice } from "../components/ui";

const CHOICES: { id: PlaysWhere; label: string; hint: string }[] = [
  { id: "home", label: "Home games", hint: "Kitchen tables and regular crews" },
  { id: "live_casino", label: "Live casino", hint: "Card rooms and casino tournaments" },
  { id: "online_cash", label: "Online cash", hint: "Ring games" },
  { id: "online_mtt", label: "Online tournaments", hint: "MTTs and sit-and-gos" },
];

/**
 * The signup question from the brief. Feeds the anonymised aggregates so we can see
 * which audience the product actually serves.
 *
 * Skippable on purpose: a mandatory question before someone has seen anything costs
 * more signups than the answer is worth, and a forced answer is a guessed answer.
 */
export function Onboarding({ onDone }: { onDone: () => void }) {
  const { getToken } = useAuth();
  const [where, setWhere] = useState<PlaysWhere | null>(null);
  const [stakes, setStakes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!where) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateProfile(getToken, { playsWhere: where, ...(stakes.trim() ? { stakes: stakes.trim() } : {}) });
      onDone();
    } catch {
      setError("Couldn't save that. Try again, or skip — you can set it later in your account.");
      setSaving(false);
    }
  }

  return (
    <Card className="card-wide">
      <h1>Where do you mostly play?</h1>
      <p className="lede">
        One question, so the trainer leads with the spots you actually face. You can change it
        whenever you like.
      </p>

      <div className="choices">
        {CHOICES.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`choice${where === c.id ? " choice-on" : ""}`}
            aria-pressed={where === c.id}
            onClick={() => setWhere(c.id)}
          >
            <span className="choice-label">{c.label}</span>
            <span className="choice-hint">{c.hint}</span>
          </button>
        ))}
      </div>

      <label className="field field-narrow">
        <span>Stakes — optional</span>
        <input
          value={stakes}
          onChange={(e) => setStakes(e.target.value.slice(0, 40))}
          placeholder="1/2, $55 MTTs, microstakes…"
          autoComplete="off"
        />
      </label>

      {error && <Notice kind="err">{error}</Notice>}

      <div className="row">
        <button className="btn" disabled={!where || saving} onClick={() => void save()}>
          {saving ? "Saving…" : "Continue"}
        </button>
        <button className="btn-quiet" onClick={onDone} disabled={saving}>
          Skip for now
        </button>
      </div>
    </Card>
  );
}
